// 1-D Dynamic Programming — Basic vs Optimized code for every "Beginner Problems" drill listed
// in the 1-D DP Explanation modal. Same shape as backtrackingDrills.js: concise steps + an
// interactive "watch it happen" visualizer per version, reusing ApproachVisualizer's array-row support.
// Some drills cross-reference the curated problems instead of duplicating content.

const dp1dDrills = [
  {
    id: 'fibonacci-number',
    title: 'Fibonacci Number',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'Directly translate the definition F(n) = F(n-1) + F(n-2) into recursive calls. Correct, but F(3) gets recomputed from scratch every time it\'s needed by both F(5) and F(4) — the same subproblem is solved over and over, doubling in redundant work with every extra step of n.',
        steps: [
          'Base case: F(0) = 0, F(1) = 1.',
          'Otherwise, recursively compute F(n-1) and F(n-2), and add them.',
          'Every call to F(k) for k < n-1 gets independently re-triggered by multiple different parent calls.',
          'The number of redundant calls grows exponentially with n.',
        ],
        code:
`int fibBasic(int n) {
    if (n <= 1) return n;
    return fibBasic(n - 1) + fibBasic(n - 2);
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up With Two Rolling Variables',
        explanation: 'Build the sequence forward from F(0) and F(1), keeping only the last two values in memory at any moment — each one is computed exactly ONCE and never recomputed, since later values only ever need the two immediately before them.',
        steps: [
          'Start with prev2 = F(0) = 0 and prev1 = F(1) = 1.',
          'For each step from 2 up to n, compute the new value as prev1 + prev2.',
          'Slide the window forward: what was prev1 becomes prev2, and the new value becomes prev1.',
          'After the loop, prev1 holds F(n) — computed in one single pass, no recursion, no repeated work.',
        ],
        visual: {
          arrays: [{ label: 'dp (fib table)', values: [0, 1] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp (fib table)': [0, 1, 1] }, highlights: { 'dp (fib table)': [2] }, note: 'F(2) = F(1) + F(0) = 1 + 0 = 1.' },
            { snapshots: { 'dp (fib table)': [0, 1, 1, 2] }, highlights: { 'dp (fib table)': [3] }, note: 'F(3) = F(2) + F(1) = 1 + 1 = 2.' },
            { snapshots: { 'dp (fib table)': [0, 1, 1, 2, 3] }, highlights: { 'dp (fib table)': [4] }, note: 'F(4) = F(3) + F(2) = 2 + 1 = 3.' },
            { snapshots: { 'dp (fib table)': [0, 1, 1, 2, 3, 5] }, highlights: { 'dp (fib table)': [5] }, note: 'F(5) = F(4) + F(3) = 3 + 2 = 5 — the final answer for n=5, reached with zero repeated work.' },
          ],
        },
        code:
`int fib(int n) {
    if (n <= 1) return n;
    int prev2 = 0, prev1 = 1;
    for (int i = 2; i <= n; i++) {
        int cur = prev1 + prev2;
        prev2 = prev1;
        prev1 = cur;
    }
    return prev1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'climbing-stairs-drill',
    title: 'Climbing Stairs',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Climbing Stairs\" there for the complete step-by-step and both code versions. You can also try it hands-on in " +
      "the \"Climbing Stairs\" mode of the Interactive Practice playground with any step count from 1 to 12.",
  },
  {
    id: 'min-cost-climbing-stairs',
    title: 'Min Cost Climbing Stairs',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Naive Recursion From Each Step',
        explanation: 'Recursively compute the minimum cost to finish from step i, trying both a 1-step and a 2-step move onward, starting the whole search from either step 0 or step 1 (both free). Correct, but "the cost to finish from step i" gets recomputed independently every time a different earlier step reaches it.',
        steps: [
          'Define climb(i): the minimum cost to reach the top starting from step i.',
          'Base case: if i is at or past the top, no more cost is needed — return 0.',
          'Otherwise, pay cost[i], then take the cheaper of a 1-step or a 2-step move onward.',
          'The answer is the cheaper of starting at step 0 or step 1 (both allowed for free).',
        ],
        code:
`int minCostClimbingStairsBasic(int[] cost) {
    int n = cost.length;
    return Math.min(climb(0, cost, n), climb(1, cost, n));
}

private int climb(int i, int[] cost, int n) {
    if (i >= n) return 0;
    return cost[i] + Math.min(climb(i + 1, cost, n), climb(i + 2, cost, n));
}`,
        complexity: { time: 'O(2ⁿ) — overlapping subproblems recomputed repeatedly', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up DP',
        explanation: 'Build a table where dp[i] is the minimum cost to REACH step i (not finish from it) — the top of the staircase is step n, one past the last cost value. Every dp[i] is computed exactly once, directly from the two steps just before it.',
        steps: [
          'dp[0] = dp[1] = 0 — both starting steps are free.',
          'For each step i from 2 to n, dp[i] = the cheaper of arriving via step i-1 (pay cost[i-1]) or step i-2 (pay cost[i-2]).',
          'dp[n] — one past the last real step — is the top of the staircase.',
          'Return dp[n] directly; no recursion, no repeated computation.',
        ],
        visual: {
          arrays: [{ label: 'dp[0..3] — min cost to REACH step i (cost = [10,15,20])', values: [0, 0] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..3] — min cost to REACH step i (cost = [10,15,20])': [0, 0, 10] }, highlights: { 'dp[0..3] — min cost to REACH step i (cost = [10,15,20])': [2] }, note: 'dp[2] = min(dp[1] + cost[1], dp[0] + cost[0]) = min(0 + 15, 0 + 10) = 10.' },
            { snapshots: { 'dp[0..3] — min cost to REACH step i (cost = [10,15,20])': [0, 0, 10, 15] }, highlights: { 'dp[0..3] — min cost to REACH step i (cost = [10,15,20])': [3] }, note: 'dp[3] = min(dp[2] + cost[2], dp[1] + cost[1]) = min(10 + 20, 0 + 15) = 15 — this is the top of the staircase, and the final answer.' },
          ],
        },
        code:
`int minCostClimbingStairs(int[] cost) {
    int n = cost.length;
    int[] dp = new int[n + 1];
    for (int i = 2; i <= n; i++) {
        dp[i] = Math.min(dp[i - 1] + cost[i - 1], dp[i - 2] + cost[i - 2]);
    }
    return dp[n];
}`,
        complexity: { time: 'O(n)', space: 'O(n) — or O(1) with two rolling variables' },
      },
    ],
  },
  {
    id: 'house-robber-drill',
    title: 'House Robber',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"House Robber\" there for the complete step-by-step and both code versions. You can also try it hands-on in " +
      "the \"House Robber\" mode of the Interactive Practice playground with any list of house values.",
  },
  {
    id: 'delete-and-earn',
    title: 'Delete and Earn',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Recurse Directly on Values, Recomputing Sums Each Call',
        explanation: 'Recursively decide, for each distinct value from highest to lowest, whether to "take" it (earn the sum of every occurrence of it, but this forbids taking value-1 entirely) or "skip" it. Correct, but each call rescans the whole input array from scratch just to total up one value\'s occurrences, and there\'s no memoization — the same value\'s decision gets recomputed on every path that reaches it.',
        steps: [
          'For the current value v, base case: if v ≤ 0, there\'s nothing left to earn — return 0.',
          'Rescan the entire input array to sum up every occurrence of v (repeated, wasted work every call).',
          'take = that sum + the best result from v - 2 (since taking v forbids v - 1 entirely).',
          'skip = the best result from v - 1. Return the larger of the two.',
        ],
        code:
`int deleteAndEarnBasic(int[] nums) {
    int maxVal = 0;
    for (int num : nums) maxVal = Math.max(maxVal, num);
    return solve(maxVal, nums);
}

private int solve(int v, int[] nums) {
    if (v <= 0) return 0;
    int sumV = 0;
    for (int num : nums) if (num == v) sumV += num;   // rescans nums every single call
    int take = sumV + solve(v - 2, nums);
    int skip = solve(v - 1, nums);
    return Math.max(take, skip);
}`,
        complexity: { time: 'Exponential — no memoization, plus an O(n) rescan per call', space: 'O(maxVal) — recursion depth' },
      },
      {
        name: 'Optimized — Bucket Sums Once, Then House-Robber-Style DP',
        explanation: 'Precompute the total earnable from each distinct value ONCE into a bucket array, then recognize the rest as exactly the House Robber problem: taking value v forbids v - 1 (its "neighbor"), so the same skip/take DP applies directly to the bucket array.',
        steps: [
          'Build a bucket array sums[] where sums[v] = v × (how many times v appears) — one linear pass, computed once.',
          'dp[v] = the best total achievable using only values 1..v.',
          'dp[v] = max(dp[v-1] (skip v entirely), dp[v-2] + sums[v] (take v, forbidding v-1)) — identical shape to House Robber.',
          'The final answer is dp[maxVal].',
        ],
        visual: {
          arrays: [
            { label: 'sums[1..4] (bucketed earnings, nums=[3,4,2])', values: [0, 2, 3, 4] },
            { label: 'dp[1..4] — best total using values 1..v', values: [0] },
          ],
          varNames: [],
          steps: [
            { note: 'Bucket sums built once from nums=[3,4,2]: sums[1]=0, sums[2]=2, sums[3]=3, sums[4]=4 (each value × its count).' },
            { snapshots: { 'dp[1..4] — best total using values 1..v': [0, 0] }, note: 'dp[1] = sums[1] = 0 — no 1s were present.' },
            { snapshots: { 'dp[1..4] — best total using values 1..v': [0, 0, 2] }, note: 'dp[2] = max(dp[1], sums[2]) = max(0, 2) = 2.' },
            { snapshots: { 'dp[1..4] — best total using values 1..v': [0, 0, 2, 3] }, note: 'dp[3] = max(dp[2], dp[1] + sums[3]) = max(2, 0 + 3) = 3.' },
            { snapshots: { 'dp[1..4] — best total using values 1..v': [0, 0, 2, 3, 6] }, note: 'dp[4] = max(dp[3], dp[2] + sums[4]) = max(3, 2 + 4) = 6 — final answer: earn 2 and 4 (skip 3), totaling 6.' },
          ],
        },
        code:
`int deleteAndEarn(int[] nums) {
    int maxVal = 0;
    for (int num : nums) maxVal = Math.max(maxVal, num);
    int[] sums = new int[maxVal + 1];
    for (int num : nums) sums[num] += num;   // bucket sums built ONCE

    int[] dp = new int[maxVal + 1];
    dp[1] = sums[1];
    for (int v = 2; v <= maxVal; v++) {
        dp[v] = Math.max(dp[v - 1], dp[v - 2] + sums[v]);
    }
    return dp[maxVal];
}`,
        complexity: { time: 'O(n + maxVal)', space: 'O(maxVal)' },
      },
    ],
  },
  {
    id: 'perfect-squares',
    title: 'Perfect Squares',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'Recursively try subtracting every perfect square ≤ n, and take 1 + the best result on what remains. Correct, but the same remaining value gets reached — and re-solved from scratch — through many different combinations of squares subtracted in different orders.',
        steps: [
          'Base case: numSquares(0) = 0 — nothing left to cover.',
          'Otherwise, try every square j² that fits within n.',
          'Recursively solve for n - j², and take 1 + the best of those results.',
          'The same value of "n minus some squares" is reached repeatedly via different subtraction orders, with no memory of prior work.',
        ],
        code:
`int numSquaresBasic(int n) {
    if (n == 0) return 0;
    int best = Integer.MAX_VALUE;
    for (int j = 1; j * j <= n; j++) {
        best = Math.min(best, 1 + numSquaresBasic(n - j * j));
    }
    return best;
}`,
        complexity: { time: 'Exponential — heavy overlap between recursive branches', space: 'O(√n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up DP (Unbounded, Like Coin Change)',
        explanation: 'This is Coin Change wearing a different costume: the "coins" are perfect squares (1, 4, 9, 16, …) and repetition is allowed. Build dp[0..n] bottom-up, and each dp[i] is finalized exactly once.',
        steps: [
          'dp[0] = 0 — zero squares needed to make 0.',
          'For each amount i from 1 to n, try every square j² ≤ i.',
          'dp[i] = 1 + the smallest dp[i - j²] found across all valid j.',
          'Return dp[n] — the fewest perfect squares summing to n.',
        ],
        visual: {
          arrays: [{ label: 'dp[0..6] — fewest perfect squares summing to i', values: [0] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1] }, note: 'dp[1] = dp[0] + 1 = 1 (only square available: 1²).' },
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1, 2] }, note: 'dp[2] = dp[1] + 1 = 2 (1² + 1²).' },
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1, 2, 3] }, note: 'dp[3] = dp[2] + 1 = 3 (1² + 1² + 1²).' },
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1, 2, 3, 1] }, note: 'dp[4]: try 1² → dp[3]+1=4; try 2² → dp[0]+1=1. Best: 1 (a single 2² = 4).' },
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1, 2, 3, 1, 2] }, note: 'dp[5]: try 1² → dp[4]+1=2; try 2² → dp[1]+1=2. Best: 2 (4 + 1).' },
            { snapshots: { 'dp[0..6] — fewest perfect squares summing to i': [0, 1, 2, 3, 1, 2, 3] }, note: 'dp[6]: try 1² → dp[5]+1=3; try 2² → dp[2]+1=3. Best: 3 (e.g., 4 + 1 + 1).' },
          ],
        },
        code:
`int numSquares(int n) {
    int[] dp = new int[n + 1];
    Arrays.fill(dp, Integer.MAX_VALUE);
    dp[0] = 0;
    for (int i = 1; i <= n; i++) {
        for (int j = 1; j * j <= i; j++) {
            dp[i] = Math.min(dp[i], dp[i - j * j] + 1);
        }
    }
    return dp[n];
}`,
        complexity: { time: 'O(n√n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'coin-change-drill',
    title: 'Coin Change',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Coin Change\" there for the complete step-by-step and both code versions. You can also try it hands-on in " +
      "the \"Coin Change\" mode of the Interactive Practice playground with any coin set and target amount.",
  },
  {
    id: 'longest-increasing-subsequence-drill',
    title: 'Longest Increasing Subsequence',
    level: 'Advanced',
    crossReference:
      "This drill's full walkthrough — including a third, even faster O(n log n) approach — lives in this " +
      "pattern's curated problem list — open \"Longest Increasing Subsequence\" there for the complete " +
      "step-by-step. You can also try the O(n²) version hands-on in this pattern's Interactive Practice playground.",
  },
  {
    id: 'combination-sum-iv',
    title: 'Combination Sum IV (Order Matters)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Brute-Force Recursion Over Every Ordered Sequence',
        explanation: 'Recursively try every number as the NEXT pick, subtracting it from the remaining target — since order matters here, [1,3] and [3,1] are counted as two different sequences. Correct, but "count the ways to reach a given remaining target" gets independently re-solved every time a different earlier choice arrives at the same remaining value.',
        steps: [
          'Base case: if target == 0, exactly one way was just completed — count it.',
          'Base case: if target < 0, this path overshot — count zero ways.',
          'Otherwise, try each number in nums as the next pick, and sum the ways from each resulting smaller target.',
          'The same "ways to reach target T" question gets asked repeatedly, from many different paths.',
        ],
        code:
`int combinationSum4Basic(int[] nums, int target) {
    if (target == 0) return 1;
    if (target < 0) return 0;
    int ways = 0;
    for (int num : nums) {
        ways += combinationSum4Basic(nums, target - num);
    }
    return ways;
}`,
        complexity: { time: 'Exponential — overlapping "ways to reach T" subproblems recomputed', space: 'O(target) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up DP, Counting Ways Directly',
        explanation: 'Build dp[0..target] where dp[i] is the number of ordered sequences summing exactly to i. Each dp[i] is computed once, by summing dp[i - num] over every number — the exact same recurrence as the brute force, just computed bottom-up with no repeated work.',
        steps: [
          'dp[0] = 1 — there\'s exactly one way to make target 0: pick nothing.',
          'For each amount i from 1 to target, try every number in nums.',
          'If the number fits (num ≤ i), add dp[i - num] to dp[i] — every way of reaching the smaller target now extends with this number as its next pick.',
          'Return dp[target].',
        ],
        visual: {
          arrays: [{ label: 'dp[0..4] — ways to reach each sum (nums=[1,2,3], order matters)', values: [1] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..4] — ways to reach each sum (nums=[1,2,3], order matters)': [1, 1] }, note: 'dp[1] = dp[0] (pick 1) = 1.' },
            { snapshots: { 'dp[0..4] — ways to reach each sum (nums=[1,2,3], order matters)': [1, 1, 2] }, note: 'dp[2] = dp[1] (pick 1) + dp[0] (pick 2) = 1 + 1 = 2.' },
            { snapshots: { 'dp[0..4] — ways to reach each sum (nums=[1,2,3], order matters)': [1, 1, 2, 4] }, note: 'dp[3] = dp[2] (pick 1) + dp[1] (pick 2) + dp[0] (pick 3) = 2 + 1 + 1 = 4.' },
            { snapshots: { 'dp[0..4] — ways to reach each sum (nums=[1,2,3], order matters)': [1, 1, 2, 4, 7] }, note: 'dp[4] = dp[3] (pick 1) + dp[2] (pick 2) + dp[1] (pick 3) = 4 + 2 + 1 = 7 — final answer, matching target=4.' },
          ],
        },
        code:
`int combinationSum4(int[] nums, int target) {
    int[] dp = new int[target + 1];
    dp[0] = 1;
    for (int i = 1; i <= target; i++) {
        for (int num : nums) {
            if (num <= i) dp[i] += dp[i - num];
        }
    }
    return dp[target];
}`,
        complexity: { time: 'O(target × nums.length)', space: 'O(target)' },
      },
    ],
  },
];

export default dp1dDrills;
